import { useState, useEffect, useCallback } from 'react';

interface UseRecaptchaReturn {
    isLoaded: boolean;
    isLoading: boolean;
    error: string | null;
    getToken: (action: string) => Promise<string>;
}

export const useRecaptcha = (): UseRecaptchaReturn => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

    useEffect(() => {
        const loadRecaptcha = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Check if reCAPTCHA is already loaded
                if (typeof (window as any).grecaptcha !== 'undefined') {
                    setIsLoaded(true);
                    setIsLoading(false);
                    return;
                }

                // Create script element
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
                script.async = true;
                script.defer = true;

                // Handle script load
                script.onload = () => {
                    // Wait for grecaptcha to be ready
                    const checkReady = () => {
                        if (
                            typeof (window as any).grecaptcha !== 'undefined' &&
                            (window as any).grecaptcha.ready
                        ) {
                            setIsLoaded(true);
                            setIsLoading(false);
                        } else {
                            setTimeout(checkReady, 100);
                        }
                    };
                    checkReady();
                };

                // Handle script error
                script.onerror = () => {
                    setError('Failed to load reCAPTCHA script');
                    setIsLoading(false);
                };

                // Append script to head
                document.head.appendChild(script);

                // Cleanup function
                return () => {
                    if (script.parentNode) {
                        script.parentNode.removeChild(script);
                    }
                };
            } catch (err) {
                setError('Failed to initialize reCAPTCHA');
                setIsLoading(false);
            }
        };

        loadRecaptcha();
    }, [siteKey]);

    const getToken = useCallback(
        async (action: string): Promise<string> => {
            if (!isLoaded) {
                throw new Error('reCAPTCHA is not loaded yet');
            }

            try {
                // Wait for grecaptcha to be ready if it's not already
                if (
                    typeof (window as any).grecaptcha === 'undefined' ||
                    !(window as any).grecaptcha.ready
                ) {
                    await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => {
                            reject(new Error('reCAPTCHA failed to load within timeout'));
                        }, 10000); // 10 second timeout

                        const checkReady = () => {
                            if (
                                typeof (window as any).grecaptcha !== 'undefined' &&
                                (window as any).grecaptcha.ready
                            ) {
                                clearTimeout(timeout);
                                resolve(true);
                            } else {
                                setTimeout(checkReady, 100);
                            }
                        };
                        checkReady();
                    });
                }

                // Execute reCAPTCHA and get token
                const token = await (window as any).grecaptcha.execute(siteKey, {
                    action,
                });
                return token;
            } catch (err: any) {
                console.error('reCAPTCHA token generation failed:', err);
                throw new Error(`reCAPTCHA verification failed: ${err.message}`);
            }
        },
        [isLoaded, siteKey]
    );

    return {
        isLoaded,
        isLoading,
        error,
        getToken,
    };
};
